import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';
import { liveUpdatesService } from '../services/liveUpdatesService';

const LiveUpdatesContext = createContext(null);
const POLL_INTERVAL_MS = 20000;

export const LiveUpdatesProvider = ({ children }) => {
  const { isAuthenticated, refreshUser } = useAuth();
  const [tick, setTick] = useState(0);
  const [lastScopes, setLastScopes] = useState([]);
  const lastCheckedAtRef = useRef(null);
  const checkingRef = useRef(false);

  const pollUpdates = useCallback(async () => {
    if (!isAuthenticated || checkingRef.current) return;
    if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;

    checkingRef.current = true;
    try {
      const result = await liveUpdatesService.check(lastCheckedAtRef.current);
      lastCheckedAtRef.current = result?.checked_at || new Date().toISOString();

      if (result?.has_updates && Array.isArray(result.scopes) && result.scopes.length > 0) {
        setLastScopes(result.scopes);
        setTick((prev) => prev + 1);

        if (result.scopes.includes('user')) {
          await refreshUser();
        }
      }
    } catch (err) {
      const status = err.response?.status;
      // Backend not deployed yet — avoid spamming console every 20s
      if (status !== 404) {
        console.error('Live updates check failed:', err);
      }
    } finally {
      checkingRef.current = false;
    }
  }, [isAuthenticated, refreshUser]);

  useEffect(() => {
    if (!isAuthenticated) {
      lastCheckedAtRef.current = null;
      setLastScopes([]);
      return undefined;
    }

    lastCheckedAtRef.current = new Date().toISOString();
    pollUpdates();

    const interval = setInterval(pollUpdates, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        pollUpdates();
      }
    };

    document.addEventListener('visibilitychange', onVisible);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [isAuthenticated, pollUpdates]);

  const value = {
    tick,
    lastScopes,
    pollNow: pollUpdates,
  };

  return (
    <LiveUpdatesContext.Provider value={value}>
      {children}
    </LiveUpdatesContext.Provider>
  );
};

export const useLiveUpdates = () => {
  const context = useContext(LiveUpdatesContext);
  if (!context) {
    throw new Error('useLiveUpdates must be used within LiveUpdatesProvider');
  }
  return context;
};

export const useLiveRefresh = (callback, scopes = []) => {
  const { tick, lastScopes } = useLiveUpdates();
  const callbackRef = useRef(callback);
  const scopesKey = scopes.join(',');

  useEffect(() => {
    callbackRef.current = callback;
  }, [callback]);

  useEffect(() => {
    if (!tick || !Array.isArray(lastScopes) || lastScopes.length === 0) return;

    const shouldRefresh =
      scopes.length === 0 || scopes.some((scope) => lastScopes.includes(scope));

    if (shouldRefresh) {
      callbackRef.current();
    }
  }, [tick, scopesKey, lastScopes]);
};
